import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {Backend, PERMISSION} from '@generated-backend-types';
import {FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {of, switchMap, takeUntil, throwError} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {
  MultiSelectEnumComponent
} from '../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import Email = Backend.App.Resources.Dashboard.Administration.Email;
import EmailData = Backend.App.Data.Dashboard.Administration.EmailData;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;
import {NotEnoughPermissionsError} from '../../../../errors/NotEnoughPermissionsError';
import {HttpErrorResponse} from '@angular/common/http';
import {UserSessionService} from '../../../../services/user-session.service';
import {EmailService} from '../../../../services/api/administration/email.service';
import {ToastrService} from 'ngx-toastr';
import {FormErrorService} from '../../../../services/form-error.service';
import {DeleteCardComponent} from '../../../../components/form-inputs/delete-card/delete-card.component';
import {IfHasAllPermissionsDirective} from '../../../../directives/if-has-all-permissions.directive';
import {TabSwitcherComponent} from '../../../../components/tab-switcher/tab-switcher.component';

@Component({
  selector: 'app-authenticated-administration-emails-edit',
  templateUrl: './edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    MultiSelectEnumComponent,
    DeleteCardComponent,
    IfHasAllPermissionsDirective,
    TabSwitcherComponent
  ]
})
class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public email?: Email;
  public mode = EditModeEnum.CREATE;

  public form = new FormGroup({
    type: new FormControl(EmailTypeEnum.PATIENT_ACCEPTANCE, [Validators.required]),
    title: new FormControl('', [Validators.required]),
    is_primary: new FormControl(false),
  });
  protected readonly EmailTypeEnum = EmailTypeEnum;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userSessionService: UserSessionService,
    private readonly emailService: EmailService,
    private readonly toastrService: ToastrService,
    private readonly formErrorService: FormErrorService,
    private readonly router: Router,
    private readonly fb: NonNullableFormBuilder,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const email: Email | undefined = data['email'];

      if (email) {
        this.email = email;

        this.form.patchValue(email);
        this.mode = EditModeEnum.EDIT;
        this.cdr.markForCheck();
      }

      this.titleService.setTitle('authenticated.administration.emails.' + this.mode + '.title');
    });
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.form.valid) {
      return;
    }

    const data = this.form.value as EmailData;


    of(this.email?.id).pipe(
      switchMap(id => {
        if (id) {
          if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_TEAMMEMBER_UPDATE_ALL])) {
            throwError(() => new NotEnoughPermissionsError());
          }

          return this.emailService.update(id, data);
        }

        if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_TEAMMEMBER_CREATE_ALL])) {
          throwError(() => new NotEnoughPermissionsError());
        }

        return this.emailService.create(data);
      })
    ).subscribe({
      next: res => {
        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.toastrService.success(this.translate.instant(`authenticated.administration.emails.${this.mode}.success`));
        this.email = res.data;

        if (this.mode === EditModeEnum.CREATE) {
          this.router.navigateByUrl(`/administration/emails/${this.email.id}`).then();
        }

        this.mode = this.email?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
        this.cdr.markForCheck();
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.form.markAllAsTouched();
          this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.form, err);
        } else {
          this.toastrService.error(this.translate.instant(`authenticated.administration.emails.${this.mode}.error`));
        }
      },
    })

    this.cdr.markForCheck();
  }

  public delete() {
    if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_EMAIL_DELETE_ALL])) {
      this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
    }

    if (this.email?.id) {
      this.emailService.delete(this.email.id).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('authenticated.administration.emails.delete.success'));
          this.router.navigateByUrl('/administration/emails').then();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('authenticated.administration.emails.delete.error'));
        }
      });
    }
  }

  protected readonly PERMISSION = PERMISSION;
}

export default EditComponent
